/**
 * @module App
 * @description Our NextJS App
 */
import { useEffect } from "react";
import { useRouter } from "next/router";
import { useReportWebVitals } from "next/web-vitals";
import { withFuel } from "@/providers/fuel-provider";
import { withContentProvider } from "@/content-provider/index";
import { ready } from "@/js/utils/events";

import "@/fonts/icomoon/style.css";
import "@/styles/globals.css";
import "@/styles/bricks/all.scss";
import sendSscEvent from "@/js/utils/ssc";
import { useCohesionEvents } from "@/hooks/useCohesionEvents";
import { withCohesion } from "@/providers/cohesion-provider";
import { setMonarchMonitoring } from "@red-digital/bricks";
import { CohesionSnippet } from "@redventures/cohesion-utils-react";
import { cohesionSnippet } from "@/js/config/cohesion";
import listOfMonarchComponents from "@/monarch/listOfMonarchComponents";
import Localize from "@/resources/Localize";
import { spanishPreferred } from "@/js/utils/spanishPreferred";
import sendToAnalytics from "@/js/plugins/cwv.js";

/**
 * @function App
 * @description Our NextJS App
 */
const MyApp = ({ Component, pageProps }) => {
  const router = useRouter();

  useCohesionEvents();

  /**
   * @function loadJS
   * @description Load our global js
   */
  const loadJS = () =>
    import("@/js/main.js").then((module) => module.default());

  /**
   * @function useEffect
   * @description Kick off this modules functions
   */
  useEffect(() => {
    // Fire on route change
    router.events.on("routeChangeComplete", () => {
      // Tagular Page View
      tagular("pageView");
    });
  }, [router.events]);

  /**
   * @function useEffect
   * @description Kick off this modules functions
   */
  useEffect(() => {
    // Load scripts only on local
    if (process.env.environment === "local" || "a11y" in router.query) {
      import("@khanacademy/tota11y");
    }
  }, [router.query]);

  /**
   * @function useEffect
   * @description Kick off this modules functions
   */
  useEffect(() => {
    setMonarchMonitoring({ hasUpdatedSnippet: true });
    ready(loadJS);
  }, []);

  /**
   * @function useEffect
   * @description Send server side cookie cookie identified event
   */
  useEffect(sendSscEvent, []);

  // Send CWV metrics to Make
  useReportWebVitals(sendToAnalytics);

  return (
    <>
      <CohesionSnippet
        config={cohesionSnippet}
        src="https://cdn.cohesionapps.com/cohesion/cohesion-latest.min.js"
      />
      <Component {...pageProps} />

      {/* LocalizeJS script (needs to be in here to fire correctly) */}
      {spanishPreferred() && process.env.environment === "production" && (
        <Localize />
      )}
    </>
  );
};

const appHOC1 = withContentProvider({ App: MyApp });
const appHOC2 = withFuel({ App: appHOC1 });
const appHOC3 = withCohesion({
  App: appHOC2,
  components: listOfMonarchComponents,
});

export default appHOC3;
